import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../../../config/apiConfig';

/**
 * Printable report for Returned Product Inspection (ΕΝ1 – Γ.Δ. 44/7).
 */
const ReturnedProductInspectionReport = ({ record }) => {
  const [companyLogo, setCompanyLogo] = useState(null);
  const [logoFetchAttempted, setLogoFetchAttempted] = useState(false);

  useEffect(() => {
    if (logoFetchAttempted) return;

    const fetchCompanyLogo = async () => {
      setLogoFetchAttempted(true);
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(
          `${API_BASE_URL}/application-setting/key/companyLogo`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          },
        );

        if (response.ok) {
          const setting = await response.json();
          if (setting?.value && setting.isActive !== false) {
            let logoUrl = setting.value.trim();
            if (!logoUrl.startsWith('http://') && !logoUrl.startsWith('https://')) {
              const baseUrl = API_BASE_URL.replace('/api', '').replace(/\/$/, '');
              logoUrl = logoUrl.startsWith('/')
                ? `${baseUrl}${logoUrl}`
                : `${baseUrl}/${logoUrl}`;
            }
            setCompanyLogo(logoUrl);
          }
        }
      } catch (error) {
        console.error('Error fetching company logo:', error);
      }
    };

    fetchCompanyLogo();
  }, [logoFetchAttempted]);

  if (!record) {
    return <div>No returned product inspection data available</div>;
  }

  const formatDate = (date) => {
    if (!date) return '';
    try {
      return dayjs(date).format('DD/MM/YYYY');
    } catch {
      return date;
    }
  };

  const formatDateTime = (date) => {
    if (!date) return '';
    try {
      return dayjs(date).format('DD/MM/YYYY HH:mm');
    } catch {
      return date;
    }
  };

  const yesNoNa = (value) => ({
    yes: value === 'YES',
    no: value === 'NO',
    na: value === 'NA',
  });

  const coldChain = yesNoNa(record.coldChainCertificate);
  const verification = yesNoNa(record.verification);
  const isRelease = record.disposition === 'RELEASE';
  const isDestroy = record.disposition === 'DESTROY';
  const customerLabel = record.customer
    ? [record.customer.name, record.customer.city].filter(Boolean).join(' — ')
    : '';

  const Checkbox = ({ checked, label }) => (
    <label style={styles.checkboxLabel}>
      <input type="checkbox" checked={!!checked} readOnly style={styles.checkbox} />
      {label}
    </label>
  );

  return (
    <div className="returned-product-inspection-report print-only" style={styles.container}>
      <style>{printStyles}</style>

      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.headerLeft}>
            {companyLogo ? (
              <img
                src={companyLogo}
                alt="Company Logo"
                style={styles.logo}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : null}
          </div>
          <div style={styles.headerRight}>
            <div style={styles.documentId}>ΕΝ1 – Γ.Δ. 44/7</div>
          </div>
        </div>
      </div>

      <table style={styles.mainTable}>
        <tbody>
          <tr>
            <td colSpan="4" style={styles.titleCell}>
              RETURNED PRODUCT INSPECTION REPORT
              <div style={styles.subtitle}>ΔΕΛΤΙΟ ΕΛΕΓΧΟΥ ΕΠΙΣΤΡΕΦΟΜΕΝΩΝ</div>
            </td>
          </tr>

          <tr>
            <td style={styles.sectionCell} colSpan="4">
              1. RECEIPT INSPECTION
            </td>
          </tr>

          <tr>
            <td style={styles.labelCell}>Item Code:</td>
            <td style={styles.valueCell}>{record.itemCode || ''}</td>
            <td style={styles.labelCell}>Product Name:</td>
            <td style={styles.valueCell}>{record.productName || ''}</td>
          </tr>

          <tr>
            <td style={styles.labelCell}>Batch Number:</td>
            <td style={styles.valueCell}>{record.batchNumber || ''}</td>
            <td style={styles.labelCell}>Expiry Date:</td>
            <td style={styles.valueCell}>{formatDate(record.expiryDate)}</td>
          </tr>

          <tr>
            <td style={styles.labelCell}>Quantity:</td>
            <td style={styles.valueCell}>{record.quantity ?? ''}</td>
            <td style={styles.labelCell}>Customer:</td>
            <td style={styles.valueCell}>{customerLabel}</td>
          </tr>

          <tr>
            <td style={styles.labelCell}>Shipment Date:</td>
            <td style={styles.valueCell}>{formatDate(record.shipmentDate)}</td>
            <td style={styles.labelCell}>Return Date:</td>
            <td style={styles.valueCell}>{formatDate(record.returnDate)}</td>
          </tr>

          <tr>
            <td style={styles.labelCell} colSpan="4">
              <div style={styles.commentsLabel}>
                Cold-chain certificate (2–8°C) from customer:
              </div>
              <div style={styles.statusContainer}>
                <Checkbox checked={coldChain.yes} label="Yes" />
                <Checkbox checked={coldChain.no} label="No" />
                <Checkbox checked={coldChain.na} label="N/A" />
              </div>
            </td>
          </tr>

          <tr>
            <td style={styles.labelCell}>Temperature on receipt:</td>
            <td style={styles.valueCell}>{record.receiptTemperature || ''}</td>
            <td style={styles.labelCell}>Ice packs used:</td>
            <td style={styles.valueCell}>
              <div style={styles.statusContainer}>
                <Checkbox checked={record.icePacksUsed === true} label="Yes" />
                <Checkbox checked={record.icePacksUsed === false} label="No" />
              </div>
            </td>
          </tr>

          <tr>
            <td style={styles.labelCell} colSpan="4">
              <div style={styles.commentsLabel}>
                Packaging appearance and contents:
              </div>
              <div style={styles.statusContainer}>
                <Checkbox
                  checked={record.packagingExcellentAppearance}
                  label="Excellent appearance"
                />
                <Checkbox
                  checked={record.packagingComplete}
                  label="Complete packaging"
                />
                <Checkbox
                  checked={record.packagingSealIntact}
                  label="Authenticity seal intact"
                />
              </div>
            </td>
          </tr>

          <tr>
            <td style={styles.labelCell} colSpan="4">
              <div style={styles.commentsLabel}>Verification:</div>
              <div style={styles.statusContainer}>
                <Checkbox checked={verification.yes} label="Yes" />
                <Checkbox checked={verification.no} label="No" />
                <Checkbox checked={verification.na} label="N/A" />
              </div>
            </td>
          </tr>

          <tr>
            <td style={styles.labelCell} colSpan="4">
              <div style={styles.commentsSection}>
                <div style={styles.commentsLabel}>Comments:</div>
                <div style={styles.commentsTextArea}>{record.comments || ''}</div>
              </div>
            </td>
          </tr>

          <tr>
            <td style={styles.labelCell} colSpan="4">
              <div style={styles.qpSection}>
                <div style={styles.qpLabel}>Inspected By — Name / Signature / Date:</div>
                <div style={styles.qpSignatureLine}>
                  {record.inspectedBy?.userDisplayName || ''}
                  {record.inspectedAt
                    ? ` — ${formatDateTime(record.inspectedAt)}`
                    : ''}
                </div>
              </div>
            </td>
          </tr>

          <tr>
            <td style={styles.sectionCell} colSpan="4">
              2. DISPOSITION
            </td>
          </tr>

          <tr>
            <td style={styles.labelCell} colSpan="4">
              <div style={styles.statusContainer}>
                <Checkbox checked={isRelease} label="Release for use" />
                <Checkbox checked={isDestroy} label="Destroy" />
              </div>
            </td>
          </tr>

          <tr>
            <td style={styles.labelCell} colSpan="4">
              <div style={styles.qpSection}>
                <div style={styles.qpLabel}>Disposition By — Name / Signature / Date:</div>
                <div style={styles.qpSignatureLine}>
                  {record.dispositionBy?.userDisplayName || ''}
                  {record.dispositionAt
                    ? ` — ${formatDateTime(record.dispositionAt)}`
                    : ''}
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '210mm',
    margin: '0 auto',
    padding: '5mm',
    backgroundColor: '#fff',
    fontFamily: 'Arial, sans-serif',
    fontSize: '11pt',
    lineHeight: '1.4',
    color: '#000',
  },
  header: {
    borderBottom: '2px solid #000',
    paddingBottom: '10px',
    marginBottom: '20px',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerLeft: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'flex-start',
  },
  headerRight: {
    flex: '1 1 auto',
    textAlign: 'right',
  },
  logo: {
    maxHeight: '50px',
    maxWidth: '200px',
    height: 'auto',
    width: 'auto',
    objectFit: 'contain',
    marginRight: '20px',
  },
  documentId: {
    fontSize: '11pt',
    color: '#666',
    fontWeight: 'normal',
  },
  mainTable: {
    width: '100%',
    borderCollapse: 'collapse',
    border: '2px solid #000',
    marginBottom: '20px',
  },
  titleCell: {
    padding: '15px',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '14pt',
    border: '2px solid #000',
    backgroundColor: '#fff',
  },
  subtitle: {
    fontSize: '11pt',
    fontWeight: 'normal',
    marginTop: '4px',
  },
  sectionCell: {
    padding: '10px',
    fontWeight: 'bold',
    fontSize: '12pt',
    border: '1px solid #000',
    backgroundColor: '#f3f4f6',
  },
  labelCell: {
    padding: '10px',
    fontWeight: 'bold',
    backgroundColor: '#fff',
    border: '1px solid #000',
    verticalAlign: 'top',
    width: '25%',
  },
  valueCell: {
    padding: '10px',
    border: '1px solid #000',
    verticalAlign: 'top',
    width: '25%',
  },
  statusContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    padding: '10px 0',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'default',
    fontSize: '11pt',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'default',
    pointerEvents: 'none',
  },
  commentsSection: {
    padding: '10px 0',
  },
  commentsLabel: {
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  commentsTextArea: {
    minHeight: '60px',
    padding: '8px',
    border: '1px solid #ccc',
    backgroundColor: '#fff',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
  },
  qpSection: {
    padding: '15px 0',
  },
  qpLabel: {
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  qpSignatureLine: {
    minHeight: '40px',
    borderBottom: '1px solid #000',
    paddingBottom: '5px',
    marginTop: '10px',
  },
};

const printStyles = `
  @media print {
    @page {
      size: A4;
      margin: 5mm;
    }

    .no-print {
      display: none !important;
    }

    header, nav, aside, .sidebar, .main-header, .app-header,
    [class*="header"]:not(.returned-product-inspection-report), [class*="nav"], [class*="sidebar"] {
      display: none !important;
    }

    .returned-product-inspection-report-wrapper {
      display: block !important;
      visibility: visible !important;
      width: 100% !important;
      height: auto !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: visible !important;
    }

    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    html, body {
      margin: 0 !important;
      padding: 0 !important;
      background: white !important;
      height: auto !important;
      overflow: visible !important;
      width: 100% !important;
    }

    .returned-product-inspection-report {
      max-width: 100% !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 5mm !important;
      box-shadow: none !important;
      height: auto !important;
      overflow: visible !important;
      background: white !important;
      display: block !important;
      visibility: visible !important;
    }

    .print-only {
      display: block !important;
    }

    table {
      page-break-inside: auto;
      border-collapse: collapse;
      width: 100% !important;
    }

    tr {
      page-break-inside: auto;
    }
  }

  @media screen {
    .returned-product-inspection-report {
      box-shadow: 0 0 10px rgba(0,0,0,0.1);
      margin: 20px auto;
    }
  }
`;

export default ReturnedProductInspectionReport;
